import { getAccessToken } from '../../../../Services/getAccessToken';
import { ENVIRONMENT_URL, DATAVERSE_TABLES, RAJPURA_TABLES } from '../../../../constants/dataverse';
import { putToStore, enqueueSync } from '../../../../Services/offlineDB';

export interface SealDefectItem {
  id: string;
  type: string;
  custom?: string;
  count: number;
  notes?: string;
}

export interface SealMachineData {
  id: string;
  machineNo: string;
  sampleQty: number;
  defects: SealDefectItem[];
  totalLeaks: number;
  passedCount: number;
  passPct: number;
  passPctStr: string;
  formattedLeakageType: string;
}

export interface SealIntegritySaveParams {
  tourId: string;
  productName: string;
  sku: string;
  machines: SealMachineData[];
  userName?: string | null;
}

export interface PqiNetWeightSaveParams {
  tourId: string;
  productName: string;
  sku: string;
  standardWeight: number;
  averageWeight: number;
  giveAway: number;
  sampleWeights: number[];
  userName?: string | null;
}

export interface PqiEvaluationSample {
  id: number;
  status: string;
  category?: string;
  detail?: string;
}

export interface PqiEvaluationSaveParams {
  tourId: string;
  evaluationType: 'Product' | 'Primary' | 'Secondary' | 'CBB';
  productName: string;
  sku: string;
  pkd?: string;
  batchCode?: string;
  samples: PqiEvaluationSample[];
  userName?: string | null;
}

export interface QualityWallMemberEvaluation {
  id: string;
  name: string;
  role?: string;
  score: number; // 0 - 4
  status: 'Green Wall' | 'Yellow Wall' | 'Hot Wall';
  action: 'Sustain' | 'Action Taken' | 'Abort';
  appearanceScore?: number;
  sealingScore?: number;
  codingScore?: number;
  sensoryScore?: number;
  remarks?: string;
}

export interface QualityWallSaveParams {
  tourId: string;
  productName: string;
  sku: string;
  lineNo: string;
  batchNo?: string;
  wallType?: string;
  facilitator?: string;
  members: QualityWallMemberEvaluation[];
  overallScore: number;
  overallStatus: 'Green Wall' | 'Yellow Wall' | 'Hot Wall';
  overallAction: 'Sustain' | 'Action Taken' | 'Abort';
  remarks?: string;
  userName?: string | null;
}

export interface PapaDefectRecord {
  id: string;
  category: 'A' | 'B' | 'C';
  type: 'Product' | 'Pack';
  name: string;
  count: number;
  pct: number;
  remarks?: string;
  isOthers?: boolean;
}

export interface PapaSaveParams {
  tourId: string;
  productName: string;
  sku: string;
  sampleSize: number;
  defects: PapaDefectRecord[];
  totalDefectCount: number;
  totalDefectPct: number;
  categoryACount: number;
  categoryBCount: number;
  categoryCCount: number;
  categoryAPct: number;
  categoryBPct: number;
  categoryCPct: number;
  userName?: string | null;
}

export interface CreamPercentageSaveParams {
  cycleNum?: number;
  formData: {
    productName: string;
    machineNo: string;
    line: string;
    standardCreamPercentage: string;
    executiveName: string;
  };
  weightData: {
    sandwichWeights: string[];
    shellWeights: string[];
  };
  qualityTourId: string;
  userName: string | null;
  shiftValue?: string;
}

export interface CreamPercentagePayload {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_creampercent1: string | null;
  cr3ea_creampercent2: string | null;
  cr3ea_creampercent3: string | null;
  cr3ea_creampercent4: string | null;
  cr3ea_wtofsandwich1: string | null;
  cr3ea_wtofsandwich2: string | null;
  cr3ea_wtofsandwich3: string | null;
  cr3ea_wtofsandwich4: string | null;
  cr3ea_wtofshell1: string | null;
  cr3ea_wtofshell2: string | null;
  cr3ea_wtofshell3: string | null;
  cr3ea_wtofshell4: string | null;
  cr3ea_avg: string | null;
  cr3ea_productname: string;
  cr3ea_stdcreampercent: string;
  cr3ea_machineno: string;
  cr3ea_lineno: string;
  cr3ea_executivename: string;
  cr3ea_platform?: string;
}

// Calculate cream percentage for a single sample row
const calculateCreamPercentage = (sandwichWeight: string, shellWeight: string): string | null => {
  const sandwich = parseFloat(sandwichWeight);
  const shell = parseFloat(shellWeight);

  if (isNaN(sandwich) || isNaN(shell) || sandwich === 0) {
    return null;
  }

  return (((sandwich - shell) / sandwich) * 100).toFixed(2);
};

// Calculate average cream percentage across valid samples
const calculateAverageCreamPercentage = (creamPercentages: (string | null)[]): string | null => {
  const validPercentages = creamPercentages
    .filter(val => val !== null && val !== undefined)
    .map(val => parseFloat(val!));

  if (validPercentages.length === 0) {
    return null;
  }

  const average = validPercentages.reduce((acc, curr) => acc + curr, 0) / validPercentages.length;
  return average.toFixed(2);
};

// Format date as DD/MM/YYYY
const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const pkgOperationsService = {
  /**
   * Fetch QA Config / Product Master items for Packaging Operations from SharePoint
   */
  getConfig: async function (accessToken?: string): Promise<any[]> {
    if (navigator.onLine && accessToken) {
      const listNames = [
        "Quality-Rajpura-PackagingOperations",
        "Quality-Rajpura-PkgOps",
        "Quality-Rajpura-Packaging",
        "Quality-Rajpura"
      ];
      const { SP_SITE_URL } = await import('../../../../constants/sharepoint');

      for (const listName of listNames) {
        try {
          const itemsUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${listName}')/items?$top=5000`;
          const response = await fetch(itemsUrl, {
            headers: {
              "Accept": "application/json;odata=verbose",
              "Authorization": `Bearer ${accessToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const items = data.d?.results || [];
            if (items.length > 0) {
              try {
                localStorage.setItem("mrs_bectors_pkgops_config", JSON.stringify(items));
              } catch (e) {}
              return items;
            }
          }
        } catch (e) {
          console.warn(`Failed to fetch config from list ${listName}`, e);
        }
      }
    }

    // Fallback to localStorage cache
    try {
      const cached = localStorage.getItem("mrs_bectors_pkgops_config");
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {}
    return [];
  },

  /**
   * Extracts dynamic active products from Packaging Operations SharePoint config items,
   * optionally filtered by category (e.g. 'Cream' / 'Cream Percentage').
   */
  getProducts: function (configs?: any[], category?: string): string[] {
    let rawConfigs = configs;
    if (!rawConfigs || rawConfigs.length === 0) {
      try {
        const cached = localStorage.getItem("mrs_bectors_pkgops_config");
        if (cached) {
          rawConfigs = JSON.parse(cached);
        }
      } catch (e) {}
    }

    if (!rawConfigs || !Array.isArray(rawConfigs) || rawConfigs.length === 0) {
      return [];
    }

    const mapProducts = (items: any[]) => {
      const prods = items
        .map((item: any) => item.Title || item.ProductName || item.Product_x0020_Name || item.SKU)
        .filter((p: any) => p && typeof p === 'string' && p.trim().length > 0)
        .map((p: string) => p.trim());
      const unique = Array.from(new Set(prods)) as string[];
      unique.sort((a, b) => a.localeCompare(b));
      return unique;
    };

    const activeMasters = rawConfigs.filter((item: any) => {
      const configType = (item.ConfigType || item.Config_x0020_Type || item.ChecklistType || "").trim();
      const isActive = item.IsActive !== false && item.Is_x0020_Active !== false;
      return (configType === "Product Master" || configType === "ProductMaster") && isActive;
    });

    if (category && category.trim().length > 0) {
      const searchCat = category.toLowerCase().trim();
      const categoryFiltered = activeMasters.filter((item: any) => {
        const itemCat = (item.ProductCategory || item.Product_x0020_Category || item.Category || "").toLowerCase().trim();
        return itemCat.includes(searchCat) || searchCat.includes(itemCat);
      });

      if (categoryFiltered.length > 0) {
        return mapProducts(categoryFiltered);
      }
    }

    return mapProducts(activeMasters);
  },

  /**
   * Fetches active product master list dynamically for Packaging Operations filtered by category
   */
  fetchProducts: async function (category: string = "Cream"): Promise<string[]> {
    try {
      const tokenResult = await getAccessToken();
      const token = tokenResult?.token;
      const configs = await this.getConfig(token || undefined);
      const prods = this.getProducts(configs, category);
      if (prods && prods.length > 0) {
        return prods;
      }
    } catch (err) {
      console.warn('Error fetching Packaging Operations products:', err);
    }
    return this.getProducts(undefined, category);
  },

  /**
   * Saves Cream Percentage record to Dataverse entity `cr3ea_creams`.
   */
  saveCreamPercentageData: async (params: CreamPercentageSaveParams): Promise<boolean> => {
    try {
      const {
        cycleNum = 1,
        formData,
        weightData,
        qualityTourId,
        userName,
        shiftValue = "Shift 1"
      } = params;

      console.log(`=== [Packaging Operations] Saving Cream Percentage Data ===`, params);

      // Calculate row cream percentages
      const creamPercent1 = calculateCreamPercentage(weightData.sandwichWeights[0] || '', weightData.shellWeights[0] || '');
      const creamPercent2 = calculateCreamPercentage(weightData.sandwichWeights[1] || '', weightData.shellWeights[1] || '');
      const creamPercent3 = calculateCreamPercentage(weightData.sandwichWeights[2] || '', weightData.shellWeights[2] || '');
      const creamPercent4 = calculateCreamPercentage(weightData.sandwichWeights[3] || '', weightData.shellWeights[3] || '');

      // Calculate overall average
      const averageCream = calculateAverageCreamPercentage([creamPercent1, creamPercent2, creamPercent3, creamPercent4]);

      const timestamp = new Date().getTime();
      const productSanitized = formData.productName ? `_${formData.productName.replace(/\s+/g, '_')}` : '';
      const uniqueTitle = `CreamPercentage_${formatDate(new Date())}${productSanitized}_${timestamp}`;

      const payload: CreamPercentagePayload = {
        cr3ea_qualitytourid: qualityTourId || '',
        cr3ea_title: uniqueTitle,
        cr3ea_cycle: `Cycle-${cycleNum}`,
        cr3ea_shift: shiftValue,
        cr3ea_tourstartdate: formatDate(new Date()),
        cr3ea_observedby: userName || formData.executiveName || null,
        cr3ea_creampercent1: creamPercent1,
        cr3ea_creampercent2: creamPercent2,
        cr3ea_creampercent3: creamPercent3,
        cr3ea_creampercent4: creamPercent4,
        cr3ea_wtofsandwich1: weightData.sandwichWeights[0] || null,
        cr3ea_wtofsandwich2: weightData.sandwichWeights[1] || null,
        cr3ea_wtofsandwich3: weightData.sandwichWeights[2] || null,
        cr3ea_wtofsandwich4: weightData.sandwichWeights[3] || null,
        cr3ea_wtofshell1: weightData.shellWeights[0] || null,
        cr3ea_wtofshell2: weightData.shellWeights[1] || null,
        cr3ea_wtofshell3: weightData.shellWeights[2] || null,
        cr3ea_wtofshell4: weightData.shellWeights[3] || null,
        cr3ea_avg: averageCream,
        cr3ea_productname: formData.productName,
        cr3ea_stdcreampercent: formData.standardCreamPercentage,
        cr3ea_machineno: formData.machineNo,
        cr3ea_lineno: formData.line,
        cr3ea_executivename: formData.executiveName,
        cr3ea_platform: "PWA"
      };

      console.log('Payload for cr3ea_creams:', payload);

      if (!navigator.onLine) {
        console.log('Device is offline. Storing Cream Percentage locally...');
        await putToStore('cream_percentage', { ...payload, localId: uniqueTitle });
        await enqueueSync({
          type: 'CREAM_PERCENTAGE',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${DATAVERSE_TABLES.CREAM_PERCENTAGE}`,
          method: 'POST',
          payload
        });
        return true;
      }

      // Online Dataverse POST
      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;

      if (!accessToken) {
        throw new Error("Access token unavailable. Enqueuing for offline sync...");
      }

      const headers = {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        "Prefer": "return=representation",
        "Authorization": `Bearer ${accessToken}`
      };

      const apiUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${DATAVERSE_TABLES.CREAM_PERCENTAGE}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Dataverse POST error response:', errorText);
        throw new Error(`Dataverse save failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Successfully saved Cream Percentage data to Dataverse:', result);
      return true;
    } catch (error) {
      console.error('Error saving Cream Percentage data:', error);
      // Fallback offline queueing
      try {
        const fallbackTitle = `CreamPercentage_Offline_${new Date().getTime()}`;
        await putToStore('cream_percentage', { localId: fallbackTitle, params });
        await enqueueSync({
          type: 'CREAM_PERCENTAGE',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${DATAVERSE_TABLES.CREAM_PERCENTAGE}`,
          method: 'POST',
          payload: params
        });
      } catch (dbErr) {
        console.warn('Failed to enqueue offline fallback:', dbErr);
      }
      return false;
    }
  },

  /**
   * Fetches Cream Percentage records from Dataverse for a specific Quality Tour ID
   */
  getCreamPercentageData: async (qualityTourId: string): Promise<any[]> => {
    try {
      if (!qualityTourId) return [];

      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;

      if (!accessToken) return [];

      const queryUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${DATAVERSE_TABLES.CREAM_PERCENTAGE}?$filter=cr3ea_qualitytourid eq '${qualityTourId}'&$orderby=createdon desc`;

      const response = await fetch(queryUrl, {
        headers: {
          "Accept": "application/json",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Authorization": `Bearer ${accessToken}`
        }
      });

      if (!response.ok) return [];

      const data = await response.json();
      return data.value || [];
    } catch (e) {
      console.warn('Error fetching Cream Percentage data from Dataverse:', e);
      return [];
    }
  },

  /**
   * Saves Seal Integrity records for multiple machines (up to 10) to Dataverse entity `cr3ea_rajpura_pkgops_sealintegrities`.
   */
  saveSealIntegrityData: async (params: SealIntegritySaveParams): Promise<boolean> => {
    try {
      const { tourId, productName, sku, machines, userName } = params;
      const cleanTourId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
      const sealTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.SEAL_INTEGRITY || "cr3ea_rajpura_pkgops_sealintegrities";
      const parentTourTable = RAJPURA_TABLES.QUALITY_TOUR || "cr3ea_rajpura_quality_tours";

      console.log(`=== [Packaging Operations] Saving Seal Integrity Data (${machines.length} machines) ===`, params);

      for (let i = 0; i < machines.length; i++) {
        const m = machines[i];
        const machineNo = m.machineNo || `M/C-${String(i + 1).padStart(2, '0')}`;
        
        let formattedLeakageType = "None";
        if (m.defects && m.defects.length > 0) {
          formattedLeakageType = m.defects.map(d => {
            let label = (d.type === "Other (Custom)" || d.type === "Other")
              ? (d.custom ? `Other (${d.custom})` : "Other")
              : (d.custom ? `${d.type} (${d.custom})` : d.type);
            if (d.notes) label += ` [${d.notes}]`;
            return `${label}: ${d.count}`;
          }).join(", ");
        }

        const totalLeaks = m.totalLeaks !== undefined 
          ? m.totalLeaks 
          : (m.defects || []).reduce((acc, cur) => acc + (Number(cur.count) || 0), 0);

        const sampleQty = Number(m.sampleQty) || 10;

        const payload: any = {
          cr3ea_name: `SealIntegrity_${sku || 'NA'}_${machineNo}`,
          cr3ea_productname: productName,
          cr3ea_sku: sku || productName,
          cr3ea_machineno: machineNo,
          cr3ea_samplequantity: String(sampleQty),
          cr3ea_noofleakage: String(totalLeaks),
          cr3ea_leakagetype: formattedLeakageType,
          cr3ea_deviationstatus: totalLeaks > 0 ? "Open" : "None",
          cr3ea_actiontaken: "",
          [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
        };

        if (userName) {
          payload.cr3ea_observedby = userName;
        }

        if (!navigator.onLine) {
          console.log(`Device offline. Enqueuing Seal Integrity for Machine ${machineNo}...`);
          await enqueueSync({
            type: 'SEAL_INTEGRITY',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${sealTable}`,
            method: 'POST',
            payload
          });
          continue;
        }

        // Online Dataverse POST
        const tokenResult = await getAccessToken();
        const accessToken = tokenResult?.token;
        if (!accessToken) {
          await enqueueSync({
            type: 'SEAL_INTEGRITY',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${sealTable}`,
            method: 'POST',
            payload
          });
          continue;
        }

        const headers = {
          "Accept": "application/json",
          "Content-Type": "application/json; charset=utf-8",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Prefer": "return=representation",
          "Authorization": `Bearer ${accessToken}`
        };

        const apiUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${sealTable}`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Dataverse POST error for machine ${machineNo}:`, errorText);
          await enqueueSync({
            type: 'SEAL_INTEGRITY',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${sealTable}`,
            method: 'POST',
            payload
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Error saving Seal Integrity data:', error);
      return false;
    }
  },

  /**
   * Fetches Seal Integrity records from Dataverse for a specific Quality Tour ID
   */
  getSealIntegrityData: async (qualityTourId: string): Promise<any[]> => {
    try {
      if (!qualityTourId) return [];
      const cleanTourId = String(qualityTourId).replace(/[{}]/g, "").trim().toLowerCase();
      const sealTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.SEAL_INTEGRITY || "cr3ea_rajpura_pkgops_sealintegrities";

      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (!accessToken) return [];

      const queryUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${sealTable}?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}' or cr3ea_qualitytourid eq '${cleanTourId}'&$orderby=createdon asc`;

      const response = await fetch(queryUrl, {
        headers: {
          "Accept": "application/json",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Authorization": `Bearer ${accessToken}`
        }
      });

      if (!response.ok) return [];
      const data = await response.json();
      return data.value || [];
    } catch (e) {
      console.warn('Error fetching Seal Integrity data from Dataverse:', e);
      return [];
    }
  },

  /**
   * Saves PQI Net Weight record to Dataverse entity `cr3ea_rajpura_pkgops_pqi_netweights`.
   */
  savePqiNetWeightData: async (params: PqiNetWeightSaveParams): Promise<boolean> => {
    try {
      const { tourId, productName, sku, standardWeight, averageWeight, giveAway, sampleWeights, userName } = params;
      const cleanTourId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
      const nwTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.PQI_NET_WEIGHT || "cr3ea_rajpura_pkgops_pqi_netweights";
      const parentTourTable = RAJPURA_TABLES.QUALITY_TOUR || "cr3ea_rajpura_quality_tours";

      console.log(`=== [Packaging Operations] Saving PQI Net Weight Data (Std: ${standardWeight}g) ===`, params);

      const payload: any = {
        cr3ea_name: `PQI_NetWeight_${sku || 'Draft'}_${new Date().toISOString().slice(0, 10)}`,
        cr3ea_productname: productName,
        cr3ea_sku: sku || productName,
        cr3ea_standardweight: Number(standardWeight) || 0,
        cr3ea_averageweight: Number(averageWeight.toFixed(2)),
        cr3ea_giveaway: Number(giveAway.toFixed(2)),
        [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
      };

      if (userName) {
        payload.cr3ea_observedby = userName;
      }

      sampleWeights.forEach((w, idx) => {
        if (idx < 32) {
          payload[`cr3ea_sampleweight${idx + 1}`] = Number(Number(w).toFixed(2));
        }
      });

      if (!navigator.onLine) {
        console.log(`Device offline. Enqueuing PQI Net Weight...`);
        await enqueueSync({
          type: 'PQI_NET_WEIGHT',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${nwTable}`,
          method: 'POST',
          payload
        });
        return true;
      }

      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (!accessToken) {
        await enqueueSync({
          type: 'PQI_NET_WEIGHT',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${nwTable}`,
          method: 'POST',
          payload
        });
        return true;
      }

      const headers = {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        "Prefer": "return=representation",
        "Authorization": `Bearer ${accessToken}`
      };

      const apiUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${nwTable}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Dataverse POST error for PQI Net Weight:`, errorText);
        await enqueueSync({
          type: 'PQI_NET_WEIGHT',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${nwTable}`,
          method: 'POST',
          payload
        });
      }

      return true;
    } catch (e) {
      console.error('Error saving PQI Net Weight data:', e);
      return false;
    }
  },

  /**
   * Saves PQI Evaluation (Product, Primary, Secondary, CBB) records to Dataverse entity `cr3ea_rajpura_pkgops_pqi_evaluations`.
   */
  savePqiEvaluationData: async (params: PqiEvaluationSaveParams): Promise<boolean> => {
    try {
      const { tourId, evaluationType, productName, sku, pkd, batchCode, samples, userName } = params;
      const cleanTourId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
      const evalTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.PQI_EVALUATION || "cr3ea_rajpura_pkgops_pqi_evaluations";
      const parentTourTable = RAJPURA_TABLES.QUALITY_TOUR || "cr3ea_rajpura_quality_tours";

      console.log(`=== [Packaging Operations] Saving PQI Evaluation Data (${evaluationType}) ===`, params);

      for (let i = 0; i < samples.length; i++) {
        const s = samples[i];
        const isNotOk = s.status === 'Not Okay';

        const payload: any = {
          cr3ea_name: `PQI_${evaluationType}_S${i + 1}_${sku || 'Draft'}`,
          cr3ea_evaluationtype: evaluationType,
          cr3ea_productname: productName,
          cr3ea_sku: sku || productName,
          cr3ea_samplenumber: String(i + 1),
          cr3ea_sampleresult: s.status || "Okay",
          cr3ea_defectcategory: s.category || "",
          cr3ea_defectdetail: s.detail || "",
          cr3ea_deviationstatus: isNotOk ? "Open" : "None",
          [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
        };

        if (pkd) payload.cr3ea_pkd = pkd;
        if (batchCode) payload.cr3ea_batchcode = batchCode;
        if (userName) payload.cr3ea_observedby = userName;

        if (!navigator.onLine) {
          await enqueueSync({
            type: 'PQI_EVALUATION',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${evalTable}`,
            method: 'POST',
            payload
          });
          continue;
        }

        const tokenResult = await getAccessToken();
        const accessToken = tokenResult?.token;
        if (!accessToken) {
          await enqueueSync({
            type: 'PQI_EVALUATION',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${evalTable}`,
            method: 'POST',
            payload
          });
          continue;
        }

        const headers = {
          "Accept": "application/json",
          "Content-Type": "application/json; charset=utf-8",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Prefer": "return=representation",
          "Authorization": `Bearer ${accessToken}`
        };

        const apiUrl = `${ENVIRONMENT_URL}/api/data/v9.2/${evalTable}`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          await enqueueSync({
            type: 'PQI_EVALUATION',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${evalTable}`,
            method: 'POST',
            payload
          });
        }
      }

      return true;
    } catch (e) {
      console.error('Error saving PQI Evaluation data:', e);
      return false;
    }
  },

  /**
   * Saves Quality Wall evaluation record with individual team member scores to Dataverse.
   */
  saveQualityWallData: async (params: QualityWallSaveParams): Promise<boolean> => {
    try {
      const {
        tourId,
        productName,
        sku,
        lineNo,
        batchNo,
        facilitator,
        members,
        overallScore,
        overallStatus,
        overallAction,
        remarks,
        userName
      } = params;

      const cleanTourId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
      const wallTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.QUALITY_WALL || "cr3ea_rajpura_pkgops_qualitywalls";
      const parentTourTable = RAJPURA_TABLES.QUALITY_TOUR || "cr3ea_rajpura_quality_tours";

      console.log(`=== [Packaging Operations] Saving Quality Wall Data (${overallStatus} - Score ${overallScore}) ===`, params);

      const membersSummaryStr = JSON.stringify(members.map(m => ({
        name: m.name,
        role: m.role || 'Reviewer',
        score: m.score,
        status: m.status,
        action: m.action,
        remarks: m.remarks || ''
      })));

      const payload: any = {
        cr3ea_name: `QualityWall_${sku || 'Batch'}_${new Date().toISOString().slice(0, 10)}`,
        cr3ea_productname: productName,
        cr3ea_sku: sku || productName,
        cr3ea_lineno: lineNo,
        cr3ea_batchno: batchNo || 'N/A',
        cr3ea_walltype: overallStatus,
        cr3ea_score: Number(overallScore.toFixed(2)),
        cr3ea_action: overallAction,
        cr3ea_facilitator: facilitator || userName || null,
        cr3ea_memberspresented: membersSummaryStr,
        cr3ea_remarks: remarks || null,
        cr3ea_platform: "PWA",
        [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
      };

      if (!navigator.onLine) {
        console.log(`Device offline. Enqueuing Quality Wall...`);
        await putToStore('quality_wall', { localId: payload.cr3ea_name, ...payload });
        await enqueueSync({
          type: 'QUALITY_WALL',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${wallTable}`,
          method: 'POST',
          payload
        });
        return true;
      }

      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (!accessToken) {
        await enqueueSync({
          type: 'QUALITY_WALL',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${wallTable}`,
          method: 'POST',
          payload
        });
        return true;
      }

      const headers = {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        "Prefer": "return=representation",
        "Authorization": `Bearer ${accessToken}`
      };

      const response = await fetch(`${ENVIRONMENT_URL}/api/data/v9.2/${wallTable}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn(`Dataverse POST error for Quality Wall:`, errorText);
        await enqueueSync({
          type: 'QUALITY_WALL',
          url: `${ENVIRONMENT_URL}/api/data/v9.2/${wallTable}`,
          method: 'POST',
          payload
        });
      }

      return true;
    } catch (e) {
      console.error('Error saving Quality Wall data:', e);
      return false;
    }
  },

  /**
   * Save PAPA (Product Attributes & Packaging Appearance) evaluation and defect counts to Dataverse.
   * Target Table: cr3ea_rajpura_pkgops_papas (CHILD_PAPA)
   */
  savePapaData: async (params: PapaSaveParams): Promise<boolean> => {
    try {
      const {
        tourId,
        productName,
        sku,
        sampleSize,
        defects,
        totalDefectCount,
        totalDefectPct,
        categoryACount,
        categoryBCount,
        categoryCCount,
        categoryAPct,
        categoryBPct,
        categoryCPct,
        userName
      } = params;

      const cleanTourId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
      const papaTable = RAJPURA_TABLES.PACKAGING_OPERATIONS.PAPA || "cr3ea_rajpura_pkgops_papas";
      const parentTourTable = RAJPURA_TABLES.QUALITY_TOUR || "cr3ea_rajpura_quality_tours";

      console.log(`=== [Packaging Operations] Saving PAPA Data (Total Defects: ${totalDefectCount}, ${totalDefectPct}%) ===`, params);

      const recordsToSave: any[] = [];

      // Add individual defect rows for defects that have count > 0 or are selected
      const activeDefects = defects.filter(d => d.count > 0);
      for (const d of activeDefects) {
        recordsToSave.push({
          cr3ea_name: `PAPA_${sku || 'Batch'}_${d.name.slice(0, 50)}`,
          cr3ea_productname: productName,
          cr3ea_sku: sku || productName,
          cr3ea_noofsamples: String(sampleSize),
          cr3ea_defecttype: `${d.name} (${d.category} - ${d.type})${d.remarks ? ` [Note: ${d.remarks}]` : ''}`,
          cr3ea_defectcount: String(d.count),
          cr3ea_defectwisepercentage: `${d.pct.toFixed(2)}%`,
          cr3ea_deviationstatus: d.category === 'A' ? 'Critical Deviation' : 'Open',
          cr3ea_actiontaken: d.remarks || null,
          cr3ea_platform: "PWA",
          [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
        });
      }

      // Add overall summary row
      recordsToSave.push({
        cr3ea_name: `PAPA_Overall_${sku || 'Batch'}_${new Date().toISOString().slice(0, 10)}`,
        cr3ea_productname: productName,
        cr3ea_sku: sku || productName,
        cr3ea_noofsamples: String(sampleSize),
        cr3ea_defecttype: `Overall Summary (Cat A: ${categoryACount} [${categoryAPct.toFixed(1)}%], Cat B: ${categoryBCount} [${categoryBPct.toFixed(1)}%], Cat C: ${categoryCCount} [${categoryCPct.toFixed(1)}%])`,
        cr3ea_defectcount: String(totalDefectCount),
        cr3ea_overalldefectpercentage: `${totalDefectPct.toFixed(2)}%`,
        cr3ea_deviationstatus: categoryACount > 0 ? 'Critical Deviation' : totalDefectCount > 0 ? 'Open' : 'Pass',
        cr3ea_actiontaken: `Evaluated by ${userName || 'QA Executive'}. Total sampled: ${sampleSize}.`,
        cr3ea_platform: "PWA",
        [`cr3ea_qualitytourid@odata.bind`]: `/${parentTourTable}(${cleanTourId})`
      });

      // Handle offline mode
      if (!navigator.onLine) {
        console.log(`Device offline. Enqueuing ${recordsToSave.length} PAPA records...`);
        for (const rec of recordsToSave) {
          await putToStore('papas', { localId: rec.cr3ea_name, ...rec });
          await enqueueSync({
            type: 'PAPA',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${papaTable}`,
            method: 'POST',
            payload: rec
          });
        }
        return true;
      }

      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (!accessToken) {
        for (const rec of recordsToSave) {
          await enqueueSync({
            type: 'PAPA',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${papaTable}`,
            method: 'POST',
            payload: rec
          });
        }
        return true;
      }

      const headers = {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        "Prefer": "return=representation",
        "Authorization": `Bearer ${accessToken}`
      };

      // Upload records
      for (const rec of recordsToSave) {
        try {
          const response = await fetch(`${ENVIRONMENT_URL}/api/data/v9.2/${papaTable}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(rec)
          });

          if (!response.ok) {
            const errTxt = await response.text();
            console.warn(`Dataverse POST error for PAPA row:`, errTxt);
            await enqueueSync({
              type: 'PAPA',
              url: `${ENVIRONMENT_URL}/api/data/v9.2/${papaTable}`,
              method: 'POST',
              payload: rec
            });
          }
        } catch (e) {
          console.warn(`Fetch error for PAPA row, queueing offline:`, e);
          await enqueueSync({
            type: 'PAPA',
            url: `${ENVIRONMENT_URL}/api/data/v9.2/${papaTable}`,
            method: 'POST',
            payload: rec
          });
        }
      }

      return true;
    } catch (e) {
      console.error('Error in savePapaData:', e);
      return false;
    }
  }
};
